import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { FieldType, FieldTypeConfig, FormlyFieldProps } from "@ngx-formly/core";
import { TooltipModule } from "primeng/tooltip";
import { AddAttachmentExpiry } from "../../../../../services/transaction.types";
import { FileUploadComponent as FileUploadBase } from "../../file-upload/file-upload.component";

/* 
  Example usage:
  {
    key: "attachments",
    type: "file",
    props: {
      label: {
        en: "Attachments",
        fi: "Liitteet",
        sv: "Bilagor",
      },                                        // Optional
      description: {
        en: "Add supporting documents",
        fi: "Lisää tukevat asiakirjat",
        sv: "Lägg till stödjande dokument",
      },                                        // Optional
      required: true,                           // Optional
      fileTypes: ".pdf,.doc,.docx,.jpg,.png",   // Optional: Limit file types (default: all types)
      maxFileSize: 50000000,                     // Optional: Max file size in bytes (default is 50MB)
      expiry: {
        amount: 2,
        units: "days"                           // Can be "days" | "weeks" | "months" | "years".
      },                                        // Optional: Expiration time (default: undefined - no expiration)
    }
  }


  ////////////////////

  JSON example:
  
  {
    "key": "attachments",
    "type": "file",
    "props": {
      "label": {
        "en": "Attachments",
        "fi": "Liitteet",
        "sv": "Bilagor"
      },                                        // Optional
      "description": {
        "en": "Add supporting documents",
        "fi": "Lisää tukevat asiakirjat",
        "sv": "Lägg till stödjande dokument"
      },                                        // Optional
      "required": true,                           // Optional
      "fileTypes": ".pdf,.doc,.docx,.jpg,.png",   // Optional: Limit file types (default: all types)
      "maxFileSize": 50000000,                     // Optional: Max file size in bytes (default is 50MB)
      "expiry": {
        "amount": 2,
        "units": "days"                           // Can be "days" | "weeks" | "months" | "years".
      }                                        // Optional: Expiration time (default: undefined - no expiration)
    }
  },
*/

export interface FileUploadProps extends FormlyFieldProps {
  fileTypes?: string;
  maxFileSize?: number;
  expiry?: AddAttachmentExpiry;
}

@Component({
  selector: "app-formly-file-upload",
  templateUrl: "./file.type.html",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TooltipModule, TranslocoDirective, FileUploadBase],
})
export class FileUploadComponent extends FieldType<FieldTypeConfig<FileUploadProps>> implements OnInit {
  files: File[] = [];

  constructor(private translocoService: TranslocoService) {
    super();
  }

  ngOnInit() {
    if (this.formControl.value === null) {
      this.formControl.setValue([]);
    } else if (Array.isArray(this.formControl.value)) {
      this.files = this.formControl.value;
    }
  }

  get expiry(): AddAttachmentExpiry | undefined {
    return this.props.expiry;
  }

  get fileTypes(): string | undefined {
    return this.props.fileTypes;
  }

  get maxFileSize(): number {
    return this.props.maxFileSize || 50 * 1024 * 1024; // Default 50MB
  }

  get isRequired(): boolean {
    return this.props.required || false;
  }

  get isInvalid(): boolean {
    return this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
  }

  public onFilesChange(newFiles: File[]): void {
    if (this.files.length != newFiles.length || this.files.some((file, index) => file !== newFiles[index])) {
      this.files = newFiles;
      this.formControl.setValue(newFiles);
      this.formControl.markAsDirty();
    }
  }

  /**
   * t(app.fileUpload.expiryUnits.days)
   * t(app.fileUpload.expiryUnits.daysSingular)
   * t(app.fileUpload.expiryUnits.weeks)
   * t(app.fileUpload.expiryUnits.weeksSingular)
   * t(app.fileUpload.expiryUnits.months)
   * t(app.fileUpload.expiryUnits.monthsSingular)
   * t(app.fileUpload.expiryUnits.years)
   * t(app.fileUpload.expiryUnits.yearsSingular)
   */
  public getExpiryUnits(expiry: AddAttachmentExpiry): string {
    let expiryKey = `app.fileUpload.expiryUnits.${expiry.units}`;
    if (expiry.amount === 1) {
      expiryKey += "Singular";
    }
    return this.translocoService.translate(expiryKey);
  }
}
